import api from "../api";
import { extractItems } from "./apiList";

const SETTINGS_TTL_MS = 5 * 60 * 1000;
const THEME_TTL_MS = 5 * 60 * 1000;
const BOOTSTRAP_TTL_MS = 5 * 60 * 1000;
const HOME_TTL_MS = 5 * 60 * 1000;
const FOOTER_TTL_MS = 5 * 60 * 1000;
const PAGE_VISIBILITY_TTL_MS = 5 * 60 * 1000;

const pendingSettingsRequests = new Map();
const pendingThemeRequests = new Map();
const pendingBootstrapRequests = new Map();
const pendingFooterRequests = new Map();
const pendingPageVisibilityRequests = new Map();

const getTenantSlug = () => {
  const slug = localStorage.getItem("tenant_slug");
  return slug && String(slug).trim() ? String(slug).trim().toLowerCase() : "default";
};

const getSettingsCacheKey = () => `settings_cache:${getTenantSlug()}`;
const getThemeCacheKey = () => `theme_cache:${getTenantSlug()}`;
const getBootstrapCacheKey = (language) => `public_bootstrap:${getTenantSlug()}:${String(language || "en").toLowerCase()}`;
const getHomeCacheKey = (language) => `home_content:${getTenantSlug()}:${String(language || "en").toLowerCase()}`;
const getHomeNewsCacheKey = (language) => `home_news:${getTenantSlug()}:${String(language || "en").toLowerCase()}`;
const getFooterCacheKey = (language) => `footer_content:${getTenantSlug()}:${String(language || "en").toLowerCase()}`;
const getPageVisibilityCacheKey = (language) => `page_visibility:${getTenantSlug()}:${String(language || "en").toLowerCase()}`;

const readCache = (key) => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;

    const expiresAt = Number(parsed.expiresAt || 0);
    if (!expiresAt || Date.now() > expiresAt) {
      localStorage.removeItem(key);
      return null;
    }

    return parsed.data ?? null;
  } catch {
    return null;
  }
};

const writeCache = (key, data, ttlMs) => {
  try {
    localStorage.setItem(
      key,
      JSON.stringify({
        data,
        expiresAt: Date.now() + ttlMs,
      })
    );
  } catch {
    // ignore cache write failures
  }
};

export const primeSettingsCache = (settingsData) => {
  if (!settingsData || typeof settingsData !== "object") return;
  writeCache(getSettingsCacheKey(), settingsData, SETTINGS_TTL_MS);
};

export const invalidateSettingsCache = () => {
  const key = getSettingsCacheKey();
  localStorage.removeItem(key);
  pendingSettingsRequests.delete(getTenantSlug());
};

export const invalidatePublicBootstrapCache = (language) => {
  const tenantSlug = getTenantSlug();
  const normalizedLang = String(language || localStorage.getItem("app_language") || "en").toLowerCase();
  const pendingKey = `${tenantSlug}:${normalizedLang}`;

  localStorage.removeItem(getBootstrapCacheKey(normalizedLang));
  localStorage.removeItem(getHomeCacheKey(normalizedLang));
  localStorage.removeItem(getHomeNewsCacheKey(normalizedLang));
  localStorage.removeItem(getFooterCacheKey(normalizedLang));
  localStorage.removeItem(getPageVisibilityCacheKey(normalizedLang));

  pendingBootstrapRequests.delete(pendingKey);
  pendingFooterRequests.delete(pendingKey);
  pendingPageVisibilityRequests.delete(pendingKey);
};

export const invalidateHomeNewsCache = (language) => {
  const normalizedLang = String(language || localStorage.getItem("app_language") || "en").toLowerCase();
  localStorage.removeItem(getHomeNewsCacheKey(normalizedLang));
};

export const invalidateAllTenantPublicCaches = () => {
  const tenantSlug = getTenantSlug();
  const prefixes = [
    `public_bootstrap:${tenantSlug}:`,
    `home_content:${tenantSlug}:`,
    `home_news:${tenantSlug}:`,
    `footer_content:${tenantSlug}:`,
    `page_visibility:${tenantSlug}:`,
  ];

  const exactKeys = [
    `settings_cache:${tenantSlug}`,
    `theme_cache:${tenantSlug}`,
  ];

  try {
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (!key) continue;

      if (exactKeys.includes(key) || prefixes.some((prefix) => key.startsWith(prefix))) {
        keysToRemove.push(key);
      }
    }

    keysToRemove.forEach((key) => localStorage.removeItem(key));
  } catch {
    // ignore storage removal failures
  }

  pendingSettingsRequests.delete(tenantSlug);
  pendingThemeRequests.delete(tenantSlug);

  for (const key of Array.from(pendingBootstrapRequests.keys())) {
    if (key.startsWith(`${tenantSlug}:`)) {
      pendingBootstrapRequests.delete(key);
    }
  }
  for (const key of Array.from(pendingFooterRequests.keys())) {
    if (key.startsWith(`${tenantSlug}:`)) {
      pendingFooterRequests.delete(key);
    }
  }
  for (const key of Array.from(pendingPageVisibilityRequests.keys())) {
    if (key.startsWith(`${tenantSlug}:`)) {
      pendingPageVisibilityRequests.delete(key);
    }
  }
};

export const getCachedSettings = async ({ forceRefresh = false } = {}) => {
  const tenantSlug = getTenantSlug();
  const cacheKey = getSettingsCacheKey();

  if (!forceRefresh) {
    const cached = readCache(cacheKey);
    if (cached) return cached;

    const language = String(localStorage.getItem("app_language") || "en").toLowerCase();
    const bootstrapCached = readCache(getBootstrapCacheKey(language));
    if (bootstrapCached?.settings) {
      writeCache(cacheKey, bootstrapCached.settings, SETTINGS_TTL_MS);
      return bootstrapCached.settings;
    }
  }

  const pending = pendingSettingsRequests.get(tenantSlug);
  if (pending) return pending;

  const language = String(localStorage.getItem("app_language") || "en").toLowerCase();
  const request = getCachedPublicBootstrap(language, { forceRefresh })
    .then((bootstrap) => {
      const data = bootstrap?.settings && typeof bootstrap.settings === "object" ? bootstrap.settings : {};
      writeCache(cacheKey, data, SETTINGS_TTL_MS);
      return data;
    })
    .catch(() =>
      api.get("/settings").then((res) => {
        const data = res?.data && typeof res.data === "object" ? res.data : {};
        writeCache(cacheKey, data, SETTINGS_TTL_MS);
        return data;
      })
    )
    .finally(() => {
      pendingSettingsRequests.delete(tenantSlug);
    });

  pendingSettingsRequests.set(tenantSlug, request);
  return request;
};

export const primeThemeCache = (themePayload) => {
  if (!themePayload || typeof themePayload !== "object") return;
  writeCache(getThemeCacheKey(), themePayload, THEME_TTL_MS);
};

export const invalidateThemeCache = () => {
  const key = getThemeCacheKey();
  localStorage.removeItem(key);
  pendingThemeRequests.delete(getTenantSlug());
};

export const getCachedTheme = async ({ forceRefresh = false } = {}) => {
  const tenantSlug = getTenantSlug();
  const cacheKey = getThemeCacheKey();

  if (!forceRefresh) {
    const cached = readCache(cacheKey);
    if (cached) return cached;
  }

  const pending = pendingThemeRequests.get(tenantSlug);
  if (pending) return pending;

  const request = api
    .get("/theme")
    .then((res) => {
      const payload = res?.data && typeof res.data === "object" ? res.data : {};
      writeCache(cacheKey, payload, THEME_TTL_MS);
      return payload;
    })
    .finally(() => {
      pendingThemeRequests.delete(tenantSlug);
    });

  pendingThemeRequests.set(tenantSlug, request);
  return request;
};

const normalizeBootstrapPayload = (payload) => {
  if (!payload || typeof payload !== "object") {
    return {
      home: {},
      footer: {},
      page_visibility: [],
      settings: {},
    };
  }

  return {
    home: payload.home && typeof payload.home === "object" ? payload.home : {},
    footer: payload.footer && typeof payload.footer === "object" ? payload.footer : {},
    page_visibility: Array.isArray(payload.page_visibility) ? payload.page_visibility : [],
    settings: payload.settings && typeof payload.settings === "object" ? payload.settings : {},
  };
};

const primeBootstrapDerivedCaches = (language, payload) => {
  const normalizedLang = String(language || "en").toLowerCase();
  const data = normalizeBootstrapPayload(payload);

  writeCache(getBootstrapCacheKey(normalizedLang), data, BOOTSTRAP_TTL_MS);
  writeCache(getHomeCacheKey(normalizedLang), data.home, HOME_TTL_MS);
  writeCache(getFooterCacheKey(normalizedLang), data.footer, FOOTER_TTL_MS);
  writeCache(getPageVisibilityCacheKey(normalizedLang), data.page_visibility, PAGE_VISIBILITY_TTL_MS);

  if (data.settings && Object.keys(data.settings).length > 0) {
    writeCache(getSettingsCacheKey(), data.settings, SETTINGS_TTL_MS);
  }

  return data;
};

export const getCachedPublicBootstrap = async (language, { forceRefresh = false } = {}) => {
  const tenantSlug = getTenantSlug();
  const normalizedLang = String(language || "en").toLowerCase();
  const pendingKey = `${tenantSlug}:${normalizedLang}`;
  const cacheKey = getBootstrapCacheKey(normalizedLang);

  if (!forceRefresh) {
    const cached = readCache(cacheKey);
    if (cached) return normalizeBootstrapPayload(cached);
  }

  const pending = pendingBootstrapRequests.get(pendingKey);
  if (pending) return pending;

  const request = api
    .get(`/public/bootstrap?lang=${encodeURIComponent(normalizedLang)}`)
    .then((res) => primeBootstrapDerivedCaches(normalizedLang, res?.data))
    .finally(() => {
      pendingBootstrapRequests.delete(pendingKey);
    });

  pendingBootstrapRequests.set(pendingKey, request);
  return request;
};

export const getCachedFooter = async (language, { forceRefresh = false } = {}) => {
  const tenantSlug = getTenantSlug();
  const normalizedLang = String(language || "en").toLowerCase();
  const pendingKey = `${tenantSlug}:${normalizedLang}`;
  const cacheKey = getFooterCacheKey(normalizedLang);

  if (!forceRefresh) {
    const cached = readCache(cacheKey);
    if (cached) return cached;

    const bootstrapCached = readCache(getBootstrapCacheKey(normalizedLang));
    if (bootstrapCached?.footer) {
      writeCache(cacheKey, bootstrapCached.footer, FOOTER_TTL_MS);
      return bootstrapCached.footer;
    }
  }

  const pending = pendingFooterRequests.get(pendingKey);
  if (pending) return pending;

  const request = getCachedPublicBootstrap(normalizedLang, { forceRefresh })
    .then((bootstrap) => bootstrap.footer || {})
    .catch(() => api.get(`/footer?lang=${encodeURIComponent(normalizedLang)}`).then((res) => res?.data || {}))
    .then((data) => {
      writeCache(cacheKey, data || {}, FOOTER_TTL_MS);
      return data || {};
    })
    .finally(() => {
      pendingFooterRequests.delete(pendingKey);
    });

  pendingFooterRequests.set(pendingKey, request);
  return request;
};

export const getCachedPageVisibility = async (language, { forceRefresh = false } = {}) => {
  const tenantSlug = getTenantSlug();
  const normalizedLang = String(language || "en").toLowerCase();
  const pendingKey = `${tenantSlug}:${normalizedLang}`;
  const cacheKey = getPageVisibilityCacheKey(normalizedLang);

  if (!forceRefresh) {
    const cached = readCache(cacheKey);
    if (cached) return Array.isArray(cached) ? cached : [];

    const bootstrapCached = readCache(getBootstrapCacheKey(normalizedLang));
    if (Array.isArray(bootstrapCached?.page_visibility)) {
      writeCache(cacheKey, bootstrapCached.page_visibility, PAGE_VISIBILITY_TTL_MS);
      return bootstrapCached.page_visibility;
    }
  }

  const pending = pendingPageVisibilityRequests.get(pendingKey);
  if (pending) return pending;

  const request = getCachedPublicBootstrap(normalizedLang, { forceRefresh })
    .then((bootstrap) => (Array.isArray(bootstrap.page_visibility) ? bootstrap.page_visibility : []))
    .catch(() => api.get("/cms/page-visibility").then((res) => extractItems(res?.data)))
    .then((data) => {
      writeCache(cacheKey, data, PAGE_VISIBILITY_TTL_MS);
      return data;
    })
    .finally(() => {
      pendingPageVisibilityRequests.delete(pendingKey);
    });

  pendingPageVisibilityRequests.set(pendingKey, request);
  return request;
};
